// =============================================
// MusicCollectionView — reusable track list for a dashboard tile
// =============================================
// Renders a flat list of tracks with play / star-rating / like / delete, a back
// button, and (for free users) ad interstitials. The player itself lives in
// MusicPage; this view calls onPlay(tracks, index) so the queue is the list shown.
// =============================================
import React, { useState } from 'react';
import { FiArrowLeft, FiPlay, FiHeart, FiTrash2, FiStar, FiEdit2 } from 'react-icons/fi';
import { AdSlot } from '../../components/ads/AdSlot';
import { TrackView, setFavorite, setRating, deleteOwnedTrack } from './musicLibrary';
import MusicCover from './MusicCover';

const AD_FREQUENCY = 8;

function fmtDuration(sec: number): string {
  if (!sec) return '—';
  const m = Math.floor(sec / 60), s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

interface Props {
  title: string;
  subtitle?: string;
  tracks: TrackView[];
  userId: number;
  onBack: () => void;
  onPlay: (tracks: TrackView[], index: number, title?: string) => void;
  currentTrackId: number | null;
  onChanged: () => void;
  showAds: boolean;
  planTier: string;
  onEditInfo?: (t: TrackView) => void;
}

export const MusicCollectionView: React.FC<Props> = ({
  title, subtitle, tracks, userId, onBack, onPlay, currentTrackId, onChanged, showAds, planTier, onEditInfo,
}) => {
  const [busyId, setBusyId] = useState<number | null>(null);

  const toggleFav = async (t: TrackView) => { await setFavorite(t.userOwnedId, !t.isFavorite); onChanged(); };
  const rate = async (t: TrackView, stars: number) => {
    await setRating(t.userOwnedId, t.rating === stars ? 0 : stars); // click same star clears
    onChanged();
  };
  const remove = async (t: TrackView) => {
    if (!window.confirm(`Remove "${t.title}" from your library?`)) return;
    setBusyId(t.userOwnedId);
    await deleteOwnedTrack(t, userId);
    setBusyId(null);
    onChanged();
  };

  return (
    <div className="mcv">
      <div className="mcv-head">
        <button className="mcv-back" onClick={onBack} title="Back"><FiArrowLeft /></button>
        <div>
          <h3 className="mcv-title">{title}</h3>
          {subtitle && <p className="mcv-subtitle">{subtitle}</p>}
        </div>
        {tracks.length > 0 && (
          <button className="mcv-playall" onClick={() => onPlay(tracks, 0, title)}><FiPlay /> Play all</button>
        )}
      </div>

      {tracks.length === 0 ? (
        <p className="music-empty">Nothing here yet.</p>
      ) : (
        <div className="music-list">
          {tracks.map((t, i) => (
            <React.Fragment key={t.userOwnedId}>
              <div className={`music-row ${currentTrackId === t.trackId ? 'playing' : ''} ${busyId === t.userOwnedId ? 'busy' : ''}`}>
                <button className="music-row-play" onClick={() => onPlay(tracks, i, title)} title="Play"><FiPlay /></button>
                <span className="music-row-num">{i + 1}</span>
                <MusicCover coverKey={t.coverKey} size={38} />
                <div className="music-row-main">
                  <span className="music-row-title">{t.title}</span>
                  <span className="music-row-sub">{t.artist}{t.album ? ` · ${t.album}` : ''}{t.mediaType === 'video' ? ' · 🎬' : ''}</span>
                </div>
                <div className="mcv-stars" title="Rate">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} className={`mcv-star ${t.rating >= n ? 'on' : ''}`} onClick={() => rate(t, n)}>
                      <FiStar />
                    </button>
                  ))}
                </div>
                <span className="music-row-dur">{fmtDuration(t.durationSeconds)}</span>
                {onEditInfo && (
                  <button className="music-row-icon" onClick={() => onEditInfo(t)} title="Edit info"><FiEdit2 /></button>
                )}
                <button className={`music-row-icon ${t.isFavorite ? 'fav' : ''}`} onClick={() => toggleFav(t)} title={t.isFavorite ? 'Unlike' : 'Like'}>
                  <FiHeart />
                </button>
                <button className="music-row-icon music-row-del" onClick={() => remove(t)} title="Remove" disabled={busyId === t.userOwnedId}>
                  <FiTrash2 />
                </button>
              </div>
              {showAds && (i + 1) % AD_FREQUENCY === 0 && i < tracks.length - 1 && (
                <AdSlot placement="music_list_interstitial" planTier={planTier} />
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default MusicCollectionView;
